import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './Sheet.module.scss';

type RadixContentProps = ComponentPropsWithoutRef<typeof RadixDialog.Content>;

/**
 * Боковая шторка: панель на всю высоту справа поверх страницы — фильтры,
 * настройки, подробности, рядом с которыми важно видеть сам список.
 *
 * Тот же примитив Radix, что у Dialog: ловушка фокуса, возврат фокуса на
 * кнопку, Escape, блокировка прокрутки под собой и `aria-modal` — только
 * геометрия другая. На телефоне шторка занимает весь экран.
 */
export const Sheet = RadixDialog.Root;

export const SheetTrigger = RadixDialog.Trigger;

export const SheetClose = RadixDialog.Close;

export interface SheetContentProps extends Omit<RadixContentProps, 'title'> {
  /** Заголовок обязателен: без него скринридер объявляет панель безымянной. */
  title: ReactNode;
  description?: ReactNode;
  /** Действия внизу — остаются на виду, пока тело прокручивается. */
  footer?: ReactNode;
  children: ReactNode;
}

export function SheetContent({
  title,
  description,
  footer,
  className,
  children,
  ...rest
}: SheetContentProps) {
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className={styles['sheet__overlay']} />
      <RadixDialog.Content className={cx(styles['sheet'], className)} {...rest}>
        <div className={styles['sheet__head']}>
          <div className={styles['sheet__heading']}>
            <RadixDialog.Title className={styles['sheet__title']}>{title}</RadixDialog.Title>
            {description !== undefined ? (
              <RadixDialog.Description className={styles['sheet__description']}>
                {description}
              </RadixDialog.Description>
            ) : (
              // Без описания Radix предупреждает в консоли — пустое и
              // скрытое снимает предупреждение, ничего не говоря лишнего.
              <RadixDialog.Description className={styles['sheet__hidden']} />
            )}
          </div>
          <RadixDialog.Close className={styles['sheet__close']} aria-label="Закрыть">
            <X size={17} />
          </RadixDialog.Close>
        </div>

        <div className={styles['sheet__body']}>{children}</div>

        {footer !== undefined && <div className={styles['sheet__footer']}>{footer}</div>}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
}
